import React from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { ModalProps } from "@/lib/type";
import { Restaurant } from "@/redux/services/restaurant/restaurant.type";
import { useCrudRestaurant } from "@/hooks/parametres/useCrudRestaurant";
import {
  useGetCountriesQuery,
  useGetCurrenciesQuery,
} from "@/redux/services/restaurant/restaurant.api";

type AddRestaurantModalProps = ModalProps<Restaurant> & {
  // Called after an owner creates a restaurant, to chain to the manager step.
  onCreated?: (restaurant: Restaurant) => void;
};

const AddRestaurantModal: React.FC<AddRestaurantModalProps> = ({
  isOpen,
  onClose,
  item,
  onCreated,
}) => {
  const { register, onSubmit, errors, isLoading, isOwner } = useCrudRestaurant(
    onClose,
    item,
    onCreated
  );

  const { data: countries } = useGetCountriesQuery(undefined, {
    skip: !!item || !isOwner,
  });
  const { data: currencies } = useGetCurrenciesQuery(undefined, {
    skip: !!item || !isOwner,
  });

  const countryOptions =
    countries?.map((c) => ({ label: c.iso2, value: c.id })) ?? [];
  const currencyOptions =
    currencies?.map((c) => ({ label: c.iso3, value: c.id })) ?? [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader className="mb-8">
            <DialogTitle>
              {item ? "Modifier le restaurant" : "Ajouter un restaurant"}
            </DialogTitle>

            <div>
              <Input
                label="Nom du restaurant"
                type="text"
                id="name"
                placeholder="Nom du restaurant"
                required
                {...register("name")}
                error={errors?.name}
                defaultValue={item?.name}
              />
            </div>
            <div>
              <Input
                label="Adresse du restaurant"
                type="text"
                id="address"
                placeholder="Adresse du restaurant"
                {...register("address")}
                error={errors?.address}
                defaultValue={item?.address}
              />
            </div>

            {!item && isOwner && (
              <>
                <div>
                  <Select
                    label="Pays"
                    id="countryId"
                    placeholder="Choisir un pays"
                    required
                    options={countryOptions}
                    {...register("countryId")}
                    error={errors?.countryId}
                  />
                </div>
                <div>
                  <Select
                    label="Devise"
                    id="currencyId"
                    placeholder="Choisir une devise"
                    required
                    options={currencyOptions}
                    {...register("currencyId")}
                    error={errors?.currencyId}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Le restaurant est créé sans manager. Vous pourrez ensuite
                  ajouter ses managers.
                </p>
              </>
            )}
          </DialogHeader>

          <DialogFooter>
            <Button
              type="submit"
              isLoading={isLoading}
            >
              {item ? "Modifier" : "Ajouter"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRestaurantModal;
